import { useQuery } from '@tanstack/react-query';
import get from 'lodash/get';

import { GetUserAccountResponse } from '@/api/userAccount/types';
import { RequestOptions, axios } from '@/common/axios';
import { ExtractFnReturnType, QueryConfig } from '@/common/react-query';
import { useGlobalStore } from '@/store';

export const getUserAccount = async (options?: RequestOptions): Promise<GetUserAccountResponse> => {
    const userAccountData = await axios.get<GetUserAccountResponse, GetUserAccountResponse>(`/profile`, {
        params: { skipRedirect: 'true' },
        ...options,
    });

    return {
        ...userAccountData,
        data: {
            ...get(userAccountData, 'data', {}),
            user: {
                ...get(userAccountData, 'data.user', {} as GetUserAccountResponse['data']['user']),
                lastName: userAccountData.data?.user?.lastName ?? '',
                phone: userAccountData.data?.user?.phone ?? '',
                organization: userAccountData.data?.user?.organization ?? '',
                address:
                    get(userAccountData, 'data.user.address', '')
                        ?.split(' ')
                        .filter(Boolean)
                        .map((word: string) => word.trim())
                        .join(' ')
                        .replace(/,/g, ', ') ?? '',
            },
        },
    };
};

type QueryFnType = typeof getUserAccount;

type UseUserAccountOptions = {
    config?: QueryConfig<QueryFnType>;
};

const UNAUTHED_RESPONSE = {
    isSuccess: false,
    isError: true,
    isPending: false,
    isLoading: false,
    isFetching: false,
    data: undefined,
    error: 'User not authenticated',
} as const;

export const useUserAccount = ({ config: { enabled = true, ...config } = {} }: UseUserAccountOptions = {}) => {
    // if we're not logged in, don't pollute the network tab with requests that can't possibly work
    // (called from, e.g. feature flag provider which is before auth is attempted)
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);

    const queryResponse = useQuery<ExtractFnReturnType<QueryFnType>>({
        ...config,
        enabled: enabled && isLoggedIn,
        queryKey: ['user-account'],
        queryFn: ({ signal }) => getUserAccount({ signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    if (isLoggedIn) {
        return queryResponse;
    }

    return UNAUTHED_RESPONSE;
};

export const useIsAdmin = (options?: UseUserAccountOptions) => {
    const { data } = useUserAccount({ ...options });
    return data?.data.user.role === 'admin';
};
